import { useQuery } from '@apollo/react-hooks';
import gql from 'graphql-tag';
import { MONITOR_LIST_TEXT } from '../../../lang';
import type { GetGSDecisionData, GSDecisionList } from './types';
import type { Paginator } from '../../../../Nomination/graphql';
import { modeOfStudyOptions } from '../../../../Application/components';
import { programmeOptions } from '../../../../Nomination';
import { PAGE_TEXT } from '../../../../Nomination/components/lang';
import { FundingSource } from '../../../../Nomination/components';

const GET_GS_DECISION_LIST = gql`
  query (
    $filter: GsDecisionFilter
    $sort: OfferDetailSorter
    $paginator: PaginatorOptions!
  ) {
    gsDecisionPaginated(filter: $filter, sort: $sort, paginator: $paginator) {
      data {
        applicationId
        applicationNo
        applicantName
        programmeType
        programmeWithDuration
        researchArea
        proposedChiefSupervisor # need to add
        studyMode
        entryYear
        department
        proposedChiefSupervisor
        applicationStatus
        programme
        fundings {
          source
        }
        submissionTime {
          from
          at
        }
        initialSubmissionTime {
          from
          at
        }
        assignee
      }
      paginator {
        total_pages
        count
      }
    }

    getGSAssigneeList {
      assigneeList {
        netId
        displayName
      }
    }
  }
`;

const PROGRAMME_LEVEL = MONITOR_LIST_TEXT.PROGRAMME_LEVEL;
const fundingSourceValues = PAGE_TEXT.FUNDING_SOURCE.FUNDING_SCHEME.VALUES;

export const useGetGSDecisionList = (
  filter: any,
  sort: any,
  paginator: Paginator,
): GetGSDecisionData => {
  const { data, loading, error, refetch } = useQuery(GET_GS_DECISION_LIST, {
    variables: { filter, sort, paginator },
    fetchPolicy: 'network-only',
  });

  let decisionListData: GSDecisionList = {
    decisionList: [],
    totalPage: 1,
    count: 0,
  };

  if (!loading && !error) {
    const applicationsContent = data?.gsDecisionPaginated?.data ?? [];
    const applications = applicationsContent.map((element: any) => {
      const { fundings } = element;
      const studymode = modeOfStudyOptions.find(
        option => option.value === element.studyMode,
      );
      const studyModeLabel = studymode?.label?.toString();
      const programmetype = programmeOptions.find(
        option => option.value === element.programmeWithDuration,
      );

      const programmeTypeLabel = programmetype?.label?.toString();
      const fundingScheme: string[] =
        (fundings ?? [])?.length === 0
          ? ['N/A']
          : fundings?.map(
              (funding: any) =>
                fundingSourceValues[
                  funding?.source as keyof typeof fundingSourceValues
                ],
            );
      const isRecommendForHKPFS = (fundings ?? [])?.some(
        (funding: any) =>
          funding?.source === FundingSource.HONGKONG_PHD_FELLOWSHIP_SCHEME,
      );

      return {
        GSAssignee: element?.assignee as string,
        ...element,
        status: element?.applicationStatus,
        progLevel:
          PROGRAMME_LEVEL[element?.programme as keyof typeof PROGRAMME_LEVEL],
        researchField: element.researchArea,
        proposedChiefSupervisor: element?.proposedChiefSupervisor,
        studyMode: studyModeLabel,
        progType: programmeTypeLabel ?? 'N/A',
        fundingScheme,
        recommendForHKPFS: isRecommendForHKPFS ? PAGE_TEXT.YES : PAGE_TEXT.NO,
      };
    });

    const totalPage = data?.gsDecisionPaginated?.paginator?.total_pages ?? 1;
    const count = data?.gsDecisionPaginated?.paginator?.count ?? 0;
    decisionListData = {
      decisionList: applications,
      totalPage,
      count,
    };
  }

  return {
    decisionListData,
    loading,
    error,
    refetch,
  };
};
